import { mapValuesDeep } from 'deepdash/standalone';
import flatten from 'flat';
import i18n from 'i18next';
import jwtDecode from 'jwt-decode';
import { camelCase, cloneDeep, get, set, unset } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import semver from 'semver';

import { generateUuid, parseURLParams, redirectToUrl, t } from '@ezbobdev/ui-framework';
import * as SDK from '@ezbobdev/ui-sdk';

import { checkIsWebView, history, isUrlExternal } from '../utils/commons';
import { FLOW_TYPE, JOURNEY_STATUS, SYSTEM_EVENTS, UI_FW_MODES } from '../utils/constants';

export const SdkActions = {
  person: {
    async createUserForPerson(personId) {
      const { actions, sdkActions } = this.state;

      try {
        const createUserForCaseResults = await SDK.Person.createUserForPerson(personId);
        SDK.Tools.setOption('userId', createUserForCaseResults.data.userId);
        const decodedToken = jwtDecode(createUserForCaseResults.data.access_token);
        actions.userState.setUserState('authorities', decodedToken.authorities);
        sdkActions.oauth.mapUserDetailsToSDKOptions(createUserForCaseResults.data);

        sdkActions.handlers.setResponses('case.createUserForPerson', createUserForCaseResults);
        return Promise.resolve(createUserForCaseResults.data);
      } catch (e) {
        sdkActions.handlers.setResponses('case.createUserForPerson', e.response);
        console.error(e);

        return Promise.reject(e);
      }
    },
  },

  case: {
    //For update case
    mapCaseFromUserData() {
      const { actions } = this.state;
      //We assume that for some edge case mapping should be the first of array of ui namespaces.
      //Therefore this function will get array of namespaces and will look for the first contained value from the namespaces.
      const caseMap = actions.settings.getUserConfigProp('caseMap') || {};
      const caseMapFlatten = flatten(caseMap);

      const newServerCaseTree = {};
      const currentStep = actions.userState.getCurrentStep();

      for (let serverProp in caseMapFlatten) {
        const uiNamespace = caseMapFlatten[serverProp];
        if (typeof uiNamespace !== `string`) continue;

        const uiValue = actions.case.getCaseValue(uiNamespace);
        const uiValueStep = actions.case.getCaseProp(uiNamespace).step;

        const nameAndValueExist = uiNamespace && uiValue !== undefined && uiValue !== '';
        const isNonCustomerWizardRoute = currentStep === undefined;
        if (nameAndValueExist && (uiValueStep === currentStep || isNonCustomerWizardRoute)) {
          set(newServerCaseTree, serverProp, uiValue);
        }
      }

      return mapValuesDeep(newServerCaseTree, (v) => {
        return typeof v === 'string' ? v.trim() : v;
      });
    },

    //For retrieve case
    mapUserDataFromCase(serverCase) {
      const { actions } = this.state;
      const caseMap = actions.settings.getUserConfigProp('caseMap');
      if (!caseMap) return;

      const flatMapServerCaseToUserData = flatten(caseMap, { safe: true });

      const newUserDataCaseTree = [];

      for (let serverProp in flatMapServerCaseToUserData) {
        const uiNamespace = flatMapServerCaseToUserData[serverProp];

        const serverValue = get(serverCase, serverProp);

        if (serverValue) {
          newUserDataCaseTree.push({
            name: uiNamespace,
            value: serverValue,
            configBy: get(this.state.userData, `${uiNamespace}.configBy`),
            validate: true,
          });
        }
      }

      actions.case.updateInCase(newUserDataCaseTree);
    },

    async create(overrideCreateCasePayload) {
      const { actions, sdkActions } = this.state;
      try {
        const session = sdkActions.case.getCaseSession();
        let step = '';
        const initStepId = actions.userState.getInitStep();
        const steps = actions.settings.getUserConfigProp('steps');
        const stepDetails = steps[initStepId];
        if (stepDetails) {
          step = stepDetails.stepName;
        }
        const caseData = {
          meta: {
            ui: {
              ...session,
              step,
              wasStepCompleted: false,
            },
          },
        };

        const casePayload =
          typeof overrideCreateCasePayload === `function` ? await overrideCreateCasePayload(caseData) : caseData;
        const createResults = await SDK.Case.create(casePayload);

        actions.case.setLatestCase(createResults.data);
        sdkActions.handlers.setResponses('case.create', createResults);

        const currentStep = get(createResults, 'data.meta.ui.step', step);
        actions.userState.setCurrentStepByName(currentStep);

        const caseToken = createResults.data.caseToken;
        const journeyUuid = get(createResults, 'data.custom.journeyUuid');

        if (journeyUuid) {
          SDK.Tools.setOption('journeyUuid', journeyUuid);
        }

        if (createResults && createResults.data) {
          SDK.Tools.setOption('caseId', createResults.data.uuid);
        }

        if (caseToken) {
          SDK.Tools.setOption('caseToken', caseToken);
          const decodedToken = jwtDecode(caseToken);
          actions.userState.setUserState('authorities', decodedToken.authorities);
        }

        return Promise.resolve(createResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async handlingCaseRetrieval(serverCase, shouldAutoMoveToStep) {
      const { actions, sdkActions } = this.state;
      delete serverCase.caseToken;

      //Fill the case
      sdkActions.case.mapUserDataFromCase(serverCase);

      //Redirect on mismatch uiVersion
      if (get(serverCase, 'meta.ui.uiVersion')) {
        const retrieveVersion = serverCase.meta.ui.uiVersion;
        if (semver.valid(retrieveVersion)) {
          const retrieveMajorVersion = semver.major(retrieveVersion);
          const currentVersion = actions.settings.getUserConfigProp('system.uiVersion');
          const currentMajorVersion = semver.major(currentVersion);

          if (retrieveMajorVersion !== currentMajorVersion) {
            actions.userState.setUserState('currentStep', 0);
            actions.userState.setUserState('validUiVersion', false);
            actions.userState.setCurrentStep(actions.userState.getInitStep());
            actions.view.togglePopupLoader(false);
            return;
          } else {
            actions.userState.setUserState('validUiVersion', true);
          }
        }
      }

      //Redirect on last step from server
      const shouldAutoMoveToStepResults =
        typeof shouldAutoMoveToStep === `function` ? await shouldAutoMoveToStep(serverCase) : shouldAutoMoveToStep;
      if (shouldAutoMoveToStepResults) {
        if (get(serverCase, 'meta.ui.step')) {
          const { step, wasStepCompleted } = serverCase.meta.ui;

          const stepDetails = actions.userState.getStepDetails(step);
          if (stepDetails) {
            //Convert step name to id
            let stepId = actions.userState.getStepDetails(step).id;
            const steps = actions.settings.getUserConfigProp('steps');

            //Redirect to dashboard if user finished the form last time
            if (stepId === steps.length - 1 && wasStepCompleted) {
              sdkActions.users.redirectToDashboard(false);
              return;
            }

            if (wasStepCompleted && stepId + 1 < steps.length) {
              stepId++;
            }
            actions.userState.setCurrentStep(stepId);
            actions.view.togglePopupLoader(false);
          } else {
            //If step not found in steps config
            actions.userState.setCurrentStep(actions.userState.getInitStep());
            actions.view.togglePopupLoader(false);
          }
        } else {
          actions.userState.setCurrentStep(actions.userState.getInitStep());
          actions.view.togglePopupLoader(false);
        }
      }
    },

    async retrieve(shouldAutoMoveToStep = true) {
      const { actions, sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      if (!caseId) return;

      try {
        const journeyUuid = SDK.Tools.getOption('journeyUuid');
        const retrieveResults = await SDK.Case.retrieve(caseId, journeyUuid);

        actions.case.setLatestCase(retrieveResults.data);
        sdkActions.handlers.setResponses('case.retrieve', retrieveResults);
        await sdkActions.case.handlingCaseRetrieval(retrieveResults.data, shouldAutoMoveToStep);

        return Promise.resolve(retrieveResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    generateCase(wasStepCompleted) {
      const { sdkActions, actions } = this.state;

      const caseFromUserData = sdkActions.case.mapCaseFromUserData();

      // Set UI
      set(caseFromUserData, 'meta.ui', sdkActions.case.getCaseUi(wasStepCompleted));

      // Set Campaigns
      set(caseFromUserData, 'transients.attributions', sdkActions.case.getCaseAttribution());

      // Set user agent
      set(caseFromUserData, 'custom.userAgent', window.navigator.userAgent);

      // Set Blackbox
      if (
        actions.settings.getUserConfigProp('system.enableDeviceFraudCheck') &&
        window.IGLOO &&
        typeof window.IGLOO.getBlackbox === `function`
      ) {
        set(caseFromUserData, 'transients.deviceInfo.deviceFraud', window.IGLOO.getBlackbox());
      }

      return caseFromUserData;
    },

    async update(wasStepCompleted = true, caseData, handleErrorMessage = true) {
      const { sdkActions, actions } = this.state;

      const caseId = SDK.Tools.getOption('caseId');
      const journeyUuid = SDK.Tools.getOption('journeyUuid');
      const deviceId = SDK.Tools.getOption('deviceId');

      if (!caseData) {
        caseData = sdkActions.case.generateCase(wasStepCompleted);
      }

      if (deviceId) {
        set(caseData, 'meta.ui.deviceId', deviceId);
      }

      try {
        const updateResults = await SDK.Case.update(caseId, caseData, journeyUuid);
        actions.case.setLatestCase(updateResults.data);
        sdkActions.handlers.setResponses('case.update', updateResults);
        return Promise.resolve(updateResults);
      } catch (e) {
        sdkActions.handlers.setResponses('case.update', e.response);

        if (e.response.data.code === `ConflictData`) {
          switch (e.response.data.message) {
            case `USER_IN_PROCESS`:
              actions.view.setAlerts([{ message: t(`serverMessages.detailedErrors.USER_IN_PROCESS`), type: 'error' }]);
              return;
            case `UPDATE_EXISTS_USER_CONFLICT`:
              actions.view.setAlerts([
                {
                  message: t(`serverMessages.detailedErrors.UPDATE_EXISTS_USER_CONFLICT`),
                  type: 'error',
                },
              ]);
              return;
            default:
              actions.view.setAlerts([
                {
                  message: t(`serverMessages.detailedErrors.UPDATE_EXISTS_USER_CONFLICT`),
                  type: 'error',
                },
              ]);
              return;
          }
        }

        if (handleErrorMessage) {
          return Promise.reject(sdkActions.handlers.serverErrors(e, undefined, 'updateCase'));
        } else {
          return Promise.reject(e);
        }
      }
    },

    getCaseUi(wasStepCompleted) {
      const { actions, sdkActions } = this.state;
      const session = sdkActions.case.getCaseSession();

      return {
        step: actions.userState.getCurrentStepName(),
        wasStepCompleted,
        uiVersion: actions.settings.getUserConfigProp('system.uiVersion') || 1,
        isFirstVisit: actions.case.getCaseValue('meta.ui.isFirstVisit'),
        ...session,
      };
    },
    getCaseAttribution() {
      return JSON.parse(localStorage.getItem('attributions')) || undefined;
    },
    getCaseSession() {
      if (!sessionStorage.getItem('sessionId')) {
        sessionStorage.setItem('sessionId', generateUuid());
      }

      if (!sessionStorage.getItem('sessionTime')) {
        sessionStorage.setItem('sessionTime', moment().format('X'));
      }

      return {
        sessionId: sessionStorage.getItem('sessionId'),
        sessionTime: Number(sessionStorage.getItem('sessionTime')),
      };
    },

    async updateCaseStep(stepName, wasStepCompleted = false) {
      const { actions, sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      const journeyUuid = SDK.Tools.getOption('journeyUuid');

      const caseData = {};
      set(caseData, 'meta.ui.step', stepName || actions.userState.getCurrentStepName());
      set(caseData, 'meta.ui.wasStepCompleted', wasStepCompleted);
      set(caseData, 'meta.ui.sessionId', sessionStorage.getItem('sessionId'));

      try {
        const updateResults = await SDK.Case.update(caseId, caseData, journeyUuid);
        sdkActions.handlers.setResponses('case.updateCaseStep', updateResults);
        actions.case.setLatestCase(updateResults.data);
        return Promise.resolve(updateResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e, undefined, 'updateCase'));
      }
    },

    async findCasesByUser(filterCases = true) {
      const { sdkActions } = this.state;
      try {
        const userId = SDK.Tools.getOption('userData') ? SDK.Tools.getOption('userData').user_id : undefined;
        if (!userId) return Promise.reject(false);
        const findCasesByUserResults = await SDK.Case.findCasesByUser(userId);
        sdkActions.handlers.setResponses('case.findCasesByUser', findCasesByUserResults);

        if (typeof filterCases === `function`) {
          const relevantCases = await filterCases(findCasesByUserResults.data);
          return Promise.resolve(relevantCases);
        } else if (filterCases === true) {
          const relevantCases = findCasesByUserResults.data.content.filter((caseData) => caseData.active);
          return Promise.resolve(relevantCases);
        } else {
          return Promise.resolve(findCasesByUserResults.data.content);
        }
      } catch (e) {
        //user can have no case in some point
        console.error('findCasesByUser', e);
        return Promise.reject(e);
      }
    },

    /**
     * findCaseByFlow
     *
     * @description Takes cases and find the relevant one based on passed flow type.
     * @param {Array} cases Cases array
     * @param {string} flow Flow type
     * @returns {object} first case in a collection or case that matches flow type
     */
    findCaseByFlow(cases, flow) {
      let relevantCase;

      if (cases.length === 1) {
        relevantCase = cases[0];
      } else {
        relevantCase = cases.find((caseItem) =>
          caseItem.fields?.journeys?.some((journey) => journey.journeyType === flow),
        );
      }

      if (!relevantCase) {
        console.error(`No cases found with flow: ${flow}.`);
        return null;
      }

      return relevantCase;
    },

    /**
     * setJourneyUuidByUserCase
     *
     * @description Takes the journeyUuuid from a case retrieved by user and persist the journeyUuid under the storage
     * @param {Object} userCase A short case details (not full onboarding case), the response of findCasesByUser
     * @returns void
     */
    setJourneyUuidByUserCase(userCase) {
      const { actions } = this.state;
      const journeys = get(userCase, 'fields.journeys');
      const journeyStrategy = actions.settings.getUserConfigProp('system.journeyStrategy');
      const flowType = (SDK.Tools.getOption('flow') ?? FLOW_TYPE.onboarding).toUpperCase();

      if (!journeys) {
        actions.view.setAlerts([{ message: t('serverMessages.generalErrors.InternalServerError'), type: 'error' }]);

        console.error('setJourneyUuidByUserCase => fields.journeys is empty');

        return;
      }

      const findJourneyByStatus = (journeys) => {
        return journeys.find((j) =>
          j.journeyStatus === userCase.active ? JOURNEY_STATUS.started : JOURNEY_STATUS.declined,
        );
      };

      let relevantJourney;

      switch (journeyStrategy) {
        case 'latest':
          const journeysSorted = journeys.sort((a, b) => new Date(a.invitationTime) - new Date(b.invitationTime));
          relevantJourney = findJourneyByStatus(journeysSorted);

          if (!relevantJourney) {
            actions.view.setAlerts([{ message: t('serverMessages.generalErrors.InternalServerError'), type: 'error' }]);

            console.error({ message: 'setJourneyUuidByUserCase => No journey with status STARTED' });

            break;
          }

          SDK.Tools.setOption('journeyUuid', relevantJourney.journeyUuid);
          SDK.Tools.setOption('flow', relevantJourney.journeyType);

          break;

        default:
          const journeysFiltered = journeys.filter((journey) => journey.journeyType === flowType);
          relevantJourney = findJourneyByStatus(journeysFiltered);

          if (!relevantJourney) {
            actions.view.setAlerts([{ message: t('serverMessages.generalErrors.InternalServerError'), type: 'error' }]);

            console.error({ message: `setJourneyUuidByUserCase => No journey with journeyType ${flowType}` });

            break;
          }

          SDK.Tools.setOption('journeyUuid', relevantJourney.journeyUuid);

          break;
      }
    },

    async attach() {
      const { sdkActions } = this.state;
      try {
        const caseId = SDK.Tools.getOption('caseId');
        const userId = SDK.Tools.getOption('userData').user_id;
        if (!caseId || !userId) return;

        const attachResults = await SDK.Case.attach(userId, caseId);
        sdkActions.handlers.setResponses('case.attach', attachResults);
        return Promise.resolve(attachResults);
      } catch (e) {
        console.error('attach', e);
        if (e.response.data.code === `ConflictData` && e.response.data.message === `USER_IN_PROCESS`) {
          sdkActions.users.redirectToDashboard(false);
          return;
        }

        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async linkAccount(linkedService, accountType, serviceName, data) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const linkAccountResults = await SDK.Case.linkAccount(
          SDK.Tools.getOption('caseId'),
          linkedService,
          accountType,
          serviceName,
          data,
        );

        sdkActions.handlers.setResponses('case.linkAccount', linkAccountResults);

        return Promise.resolve(linkAccountResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async accountCallback(method, url) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const result = await SDK.Case.accountCallback(method, url);

        sdkActions.handlers.setResponses('case.accountCallback', result);

        return Promise.resolve(result);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getQuote(indicative, waitTimeMS, feeType, repaymentType, policy) {
      const { sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');
        const getQuoteResults = await SDK.Case.getQuote(caseId, indicative, waitTimeMS, feeType, repaymentType, policy);
        sdkActions.handlers.setResponses('case.quote', getQuoteResults);
        return Promise.resolve(getQuoteResults);
      } catch (e) {
        if (indicative) {
          return Promise.reject(sdkActions.handlers.serverErrors(e));
        } else {
          return Promise.reject(e);
        }
      }
    },

    async uploadDocuments({ docType, files, shouldUseRecaptcha = true }) {
      const { actions, sdkActions } = this.state;

      const caseId = SDK.Tools.getOption('caseId');

      actions.view.clearAlerts();

      if (shouldUseRecaptcha) {
        try {
          await actions.externalServices.googleRecaptcha.generateTokenVersionThree();
          await actions.externalServices.googleRecaptcha.generateTokenVersionTwo();
        } catch (e) {
          console.error(e);
        }
      }

      const response = {
        errors: [],
        success: [],
      };

      for (let file of files) {
        try {
          const uploadDocumentResult = await SDK.Case.uploadDocument(caseId, file, docType);
          sdkActions.handlers.setResponses('case.uploadDocuments', uploadDocumentResult);
          response.success.push(uploadDocumentResult);
        } catch (e) {
          response.errors.push({ file, serverError: e });
        }
      }

      actions.externalServices.googleRecaptcha.resetRecaptchaVersionTwo();

      if (response.errors.length) {
        return Promise.reject(response.errors);
      } else {
        return Promise.resolve(response.success);
      }
    },

    async uploadDocument({ file, docType, docSubType, shouldUseRecaptcha = true }) {
      const { actions, sdkActions } = this.state;

      const caseId = SDK.Tools.getOption('caseId');

      try {
        if (shouldUseRecaptcha) {
          await actions.externalServices.googleRecaptcha.generateTokenVersionThree();
          await actions.externalServices.googleRecaptcha.generateTokenVersionTwo();
        }

        const response = await SDK.Case.uploadDocument(caseId, file, docType, docSubType);
        sdkActions.handlers.setResponses('case.uploadDocument', response);

        return Promise.resolve(response);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.externalServices.googleRecaptcha.resetRecaptchaVersionTwo();
      }
    },

    async deleteDocument(fileId, caseUuid) {
      const caseId = caseUuid || SDK.Tools.getOption('caseId');
      return SDK.Case.deleteDocument(caseId, fileId);
    },

    async getUploadedDocuments(docType, shouldFilterByUserId) {
      const { sdkActions } = this.state;
      try {
        const caseId = SDK.Tools.getOption('caseId');
        const getUploadedDocumentsResults = await SDK.Case.getUploadedDocuments(caseId, docType, shouldFilterByUserId);
        sdkActions.handlers.setResponses('case.getUploadedDocuments', getUploadedDocumentsResults);
        return getUploadedDocumentsResults.data;
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },

    async getUploadedDocumentByUuid(documentUuid, caseUuid) {
      const { actions, sdkActions } = this.state;

      const caseId = caseUuid || SDK.Tools.getOption('caseId');

      try {
        actions.view.togglePopupLoader(true);
        const documentResult = await SDK.Case.getUploadedDocumentByUuid(caseId, documentUuid);
        sdkActions.handlers.setResponses('case.getUploadedDocumentByUuid', documentResult);

        return documentResult.data;
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getZipFiles(fileNameList) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const caseId = SDK.Tools.getOption('caseId');
        const getZipFilesData = await SDK.Case.getZipFiles(caseId, fileNameList);

        sdkActions.handlers.setResponses('case.getZipFiles', getZipFilesData);

        return getZipFilesData.data;
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async progress(direction = 'FORWARD') {
      const { sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      const journeyUuid = SDK.Tools.getOption('journeyUuid');

      try {
        const progressResults = await SDK.Case.progress(caseId, journeyUuid, direction);
        sdkActions.handlers.setResponses('case.progress', progressResults);

        return Promise.resolve(progressResults);
      } catch (e) {
        console.error(e);
        sdkActions.handlers.setResponses('case.progress', e.response);

        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async createUserForCase() {
      const { actions, sdkActions } = this.state;
      const caseId = SDK.Tools.getOption(`caseId`);
      const accessToken = SDK.Tools.getOption(`accessToken`);
      if (accessToken) return Promise.resolve(accessToken);

      try {
        const createUserForCaseResults = await SDK.Case.createUserForCase(caseId);
        SDK.Tools.setOption('userId', createUserForCaseResults.data.userId);
        const decodedToken = jwtDecode(createUserForCaseResults.data.access_token);
        actions.userState.setUserState('authorities', decodedToken.authorities);
        sdkActions.oauth.mapUserDetailsToSDKOptions(createUserForCaseResults.data);

        sdkActions.handlers.setResponses('case.createUserForCase', createUserForCaseResults);
        return Promise.resolve(createUserForCaseResults);
      } catch (e) {
        if (get(e, 'response.data.details.0.violations.0.code') === 'Unique') {
          actions.view.setAlerts([{ message: t(`serverMessages.detailedErrors.Unique`), type: 'error' }]);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        console.error(e);
        sdkActions.handlers.setResponses('case.createUserForCase', e.response);
        return Promise.reject(e);
      }
    },

    async cancel() {
      const { sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      if (!caseId) return;

      try {
        const cancelResults = await SDK.Case.cancel(caseId);
        sdkActions.handlers.setResponses('case.cancel', cancelResults);
        return Promise.resolve(cancelResults);
      } catch (e) {
        console.error(e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getBankAccounts() {
      const { sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      if (!caseId) return;

      try {
        const bankAccounts = await SDK.Case.getBankAccountsFull(caseId);
        sdkActions.handlers.setResponses('case.getBankAccounts', bankAccounts);
        return Promise.resolve(bankAccounts);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    },

    async setPrimaryBankAccount(accountNumber, sortCode) {
      const { sdkActions } = this.state;
      const caseId = SDK.Tools.getOption('caseId');
      if (!caseId) return;

      try {
        const result = await SDK.Case.setPrimaryBankAccount(caseId, accountNumber, sortCode);
        sdkActions.handlers.setResponses('case.setPrimaryBankAccount', result);
        return Promise.resolve(result);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    },

    async getCaseSummary() {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const caseId = SDK.Tools.getOption('caseId');
        const response = await SDK.Case.getCaseSummary(caseId);

        sdkActions.handlers.setResponses('case.getCaseSummary', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getCaseOfferDiscount() {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const caseId = SDK.Tools.getOption('caseId');
        const response = await SDK.Case.getCaseOfferDiscount(caseId);

        sdkActions.handlers.setResponses('case.getCaseOfferDiscount', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async createFileWithData(caseUuid, data) {
      const { sdkActions, actions } = this.state;
      try {
        actions.view.togglePopupLoader(true);

        const file = await SDK.Case.createFileWithData(caseUuid, data);
        sdkActions.handlers.setResponses('case.createFileWithData', file);

        return Promise.resolve(file);
      } catch (e) {
        console.error(e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  caseContributors: {
    async getApplicantData() {
      const { actions, sdkActions } = this.state;
      try {
        const caseId = SDK.Tools.getOption('caseId');
        if (!caseId) return;

        actions.view.togglePopupLoader(true);
        const response = await SDK.CaseContributors.getApplicantData(caseId);
        sdkActions.handlers.setResponses('caseContributors.getApplicantData', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async createJourneySet(journeyType) {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        if (!journeyType) {
          throw Error('journeyType is required!');
        }

        actions.view.togglePopupLoader(true);

        const result = await SDK.CaseContributors.createJourneySet(caseId, journeyType);
        sdkActions.handlers.setResponses('caseContributors.createJourneySet', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async startJourney(caseId, flow) {
      const { sdkActions } = this.state;

      try {
        const result = await SDK.CaseContributors.startJourney(caseId, flow);

        sdkActions.handlers.setResponses('caseContributors.startJourney', result);

        return Promise.resolve(result);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async getPotentialContributors(journeySetUuid) {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        if (!journeySetUuid) {
          throw Error('Missing journeySetUuid');
        }

        actions.view.togglePopupLoader(true);
        const result = await SDK.CaseContributors.getPotentialContributors(caseId, journeySetUuid);
        sdkActions.handlers.setResponses('caseContributors.getPotentialContributors', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getInvitedContributors() {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        actions.view.togglePopupLoader(true);
        const result = await SDK.CaseContributors.getInvitedContributors(caseId);
        sdkActions.handlers.setResponses('caseContributors.getInvitedContributors', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async sendContributorInvitation(journeySetUuid, contributorToInvite) {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        if (!journeySetUuid) {
          throw Error('Missing journeySetUuid');
        }

        if (!contributorToInvite) {
          throw Error('Contributor to invite is required');
        }

        actions.view.togglePopupLoader(true);
        const result = await SDK.CaseContributors.sendContributorInvitation(
          caseId,
          journeySetUuid,
          contributorToInvite,
        );
        sdkActions.handlers.setResponses('caseContributors.sendContributorInvitation', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async resendContributorInvitation(journeySetUuid, journeyUuid) {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        if (!journeySetUuid) {
          throw Error('Missing journeySetUuid');
        }

        if (!journeyUuid) {
          throw Error('Missing journeyUuid');
        }

        actions.view.togglePopupLoader(true);
        const result = await SDK.CaseContributors.resendContributorInvitation(caseId, journeySetUuid, journeyUuid);
        sdkActions.handlers.setResponses('caseContributors.resendContributorInvitation', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async cancelContributorInvitation(journeySetUuid, journeyUuid) {
      const { actions, sdkActions } = this.state;

      try {
        const caseId = SDK.Tools.getOption('caseId');

        if (!caseId) {
          throw Error('Missing caseId');
        }

        if (!journeySetUuid) {
          throw Error('Missing journeySetUuid');
        }

        if (!journeyUuid) {
          throw Error('Missing journeyUuid');
        }

        actions.view.togglePopupLoader(true);
        const result = await SDK.CaseContributors.cancelContributorInvitation(caseId, journeySetUuid, journeyUuid);
        sdkActions.handlers.setResponses('caseContributors.cancelContributorInvitation', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async approveContributorInvitation(tokenData, guestToken) {
      const { actions, sdkActions } = this.state;
      const { case_id, journeyUuid, journeySetUuid } = tokenData;

      try {
        if (!case_id) {
          throw Error('Missing caseId');
        }

        if (!journeySetUuid) {
          throw Error('Missing journeySetUuid');
        }

        if (!journeyUuid) {
          throw Error('Missing journeyUuid');
        }

        actions.view.togglePopupLoader(true);

        const result = await SDK.CaseContributors.approveInvitation(case_id, journeySetUuid, journeyUuid, guestToken);
        sdkActions.handlers.setResponses('caseContributors.approveIntivation', result);

        return Promise.resolve(result.data);
      } catch (e) {
        console.error(e);
        sdkActions.handlers.serverErrors(e);
        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  mobileVerification: {
    async generateAndSendCode(params = {}) {
      const { sdkActions } = this.state;
      try {
        const {
          sendType = 'SMS',
          context = SDK.Tools.getOption('caseId') || SDK.Tools.getOption('invitationCaseId'),
          ...rest
        } = params;

        const generateAndSendCodeResults = await SDK.MobileVerification.generateAndSendCode({
          sendType,
          context,
          ...rest,
        });

        sdkActions.handlers.setResponses('mobileVerification.generateAndSendCode', generateAndSendCodeResults);

        return Promise.resolve(generateAndSendCodeResults);
      } catch (err) {
        console.error(`generateAndSendCode`, err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    async verifyCode(code, opToken) {
      const { sdkActions } = this.state;
      try {
        const verifyCodeResults = await SDK.MobileVerification.verifyCode(code, opToken);
        sdkActions.handlers.setResponses('mobileVerification.verifyCode', verifyCodeResults);
        return Promise.resolve(verifyCodeResults);
      } catch (err) {
        console.error(`verifyCode`, err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
  },

  address: {
    async postcodeCompletion(query) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const postcodeCompletionResults = await SDK.Address.postcodeCompletion(query, language);
        sdkActions.handlers.setResponses('address.postcodeCompletion', postcodeCompletionResults);
        return Promise.resolve(postcodeCompletionResults);
      } catch (e) {
        console.error(`postcodeCompletion`, e);
        return Promise.reject(e);
      }
    },
    async addressLookup(postcode, policy) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const addressLookupResults = await SDK.Address.addressLookup(postcode, policy, language);
        sdkActions.handlers.setResponses('address.addressLookup', addressLookupResults);
        return Promise.resolve(addressLookupResults);
      } catch (e) {
        console.error(`addressLookup`, e);
        return Promise.reject(e);
      }
    },
    async addressFormat(address) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const addressFormat = await SDK.Address.addressFormat(address, language);
        sdkActions.handlers.setResponses('address.addressFormat', addressFormat);
        return Promise.resolve(addressFormat);
      } catch (e) {
        console.error(`addressFormat`, e);
        return Promise.reject(e);
      }
    },
    async getCities(city) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const citiesResults = await SDK.Address.getCities(city, language);
        sdkActions.handlers.setResponses('address.getCities', citiesResults);
        return Promise.resolve(citiesResults);
      } catch (e) {
        console.error(`getCities`, e);
        return Promise.reject(e);
      }
    },
    async getStreets(city, street) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const streetsResults = await SDK.Address.getStreets(city, street, language);
        sdkActions.handlers.setResponses('address.getStreets', streetsResults);
        return Promise.resolve(streetsResults);
      } catch (e) {
        console.error(`getStreets`, e);
        return Promise.reject(e);
      }
    },
  },

  integration: {
    async companyLookup(query, businessType) {
      const { sdkActions } = this.state;
      try {
        const companyLookupResults = await SDK.Integration.companyLookup(query, businessType);
        sdkActions.handlers.setResponses('integration.companyLookup', companyLookupResults);
        return Promise.resolve(companyLookupResults);
      } catch (e) {
        //do not show AlertUi error message when looking for company
        return Promise.reject(e);
      }
    },

    async bankLookup(key) {
      const { sdkActions } = this.state;
      try {
        const bankLookupResults = await SDK.Integration.bankLookup(key);
        sdkActions.handlers.setResponses('integration.bankLookup', bankLookupResults);
        return Promise.resolve(bankLookupResults);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async bankBranchesLookup(key, bankCode) {
      const { sdkActions } = this.state;
      try {
        const bankBranchesLookupResults = await SDK.Integration.bankBranchesLookup(key, bankCode);
        sdkActions.handlers.setResponses('integration.bankBranchesLookup', bankBranchesLookupResults);
        return Promise.resolve(bankBranchesLookupResults);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async transcription(content) {
      const { sdkActions } = this.state;
      try {
        const transcriptionResults = await SDK.Integration.transcription(content);
        sdkActions.handlers.setResponses('integration.transcription', transcriptionResults);
        return Promise.resolve(transcriptionResults);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async getPrimeByInterestRate(interestRate) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const primeResults = await SDK.Integration.getPrimeByInterestRate(interestRate);
        sdkActions.handlers.setResponses('integration.getPrimeByInterestRate', primeResults);

        const data = { ...primeResults.data };
        const primeOperator = actions.settings.getUserConfigProp('custom.primeOperator');

        if (primeOperator === 'minus' && data.prime != null && interestRate != null) {
          data.computedInterestRate = data.prime - interestRate;
        }

        return Promise.resolve(data);
      } catch (e) {
        return Promise.reject(this.state.sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getAddressByParams(params) {
      const { sdkActions } = this.state;
      const language = i18n.language;
      try {
        const address = await SDK.Address.getAddressByParams(params, language);
        sdkActions.handlers.setResponses('address.getAddressByParams', address);

        return Promise.resolve(address.data);
      } catch (e) {
        console.log(e);
        return Promise.reject(this.state.sdkActions.handlers.serverErrors(e));
      }
    },

    async getBankBranches(data) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Integration.getBankBranches(data);

        sdkActions.handlers.setResponses('integration.bankBranches', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
  },

  officers: {
    async getShareholders(includeCompanies, type = 'SHAREHOLDERS') {
      const { actions, sdkActions } = this.state;
      const caseID = SDK.Tools.getOption('caseId');
      try {
        const getShareholdersResults = await SDK.Officers.getShareholders(caseID, includeCompanies, type);
        sdkActions.handlers.setResponses('officers.getShareholders', getShareholdersResults);
        return Promise.resolve(getShareholdersResults);
      } catch (e) {
        return Promise.reject(this.state.sdkActions.handlers.serverErrors(e));
      }
    },

    async setShareholder(data) {
      const { sdkActions } = this.state;
      const caseID = SDK.Tools.getOption('caseId');
      try {
        const setShareholderResults = await SDK.Officers.setShareholder(caseID, data, data.uuid);
        sdkActions.handlers.setResponses('officers.setShareholder', setShareholderResults);
        return Promise.resolve(setShareholderResults);
      } catch (e) {
        return Promise.reject(this.state.sdkActions.handlers.serverErrors(e));
      }
    },

    async addShareholder(data) {
      const { sdkActions } = this.state;
      const caseID = SDK.Tools.getOption('caseId');
      try {
        const addShareholderResults = await SDK.Officers.addShareholder(caseID, data);
        sdkActions.handlers.setResponses('officers.addShareholder', addShareholderResults);
        return Promise.resolve(addShareholderResults);
      } catch (e) {
        return Promise.reject(this.state.sdkActions.handlers.serverErrors(e));
      }
    },
  },

  signature: {
    async getLinks() {
      const { actions, sdkActions } = this.state;
      const caseUuid = SDK.Tools.getOption('caseId');

      try {
        if (!caseUuid) {
          throw Error('signature getLinks() => caseUuid is required');
        }

        actions.view.togglePopupLoader(true);
        const links = await SDK.Signature.getLinks(caseUuid);
        sdkActions.handlers.setResponses('signature.getLinks', links);

        return Promise.resolve(links);
      } catch (e) {
        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  users: {
    async getUserNotifications(params) {
      const { actions, sdkActions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Users.getUserNotifications(params);
        sdkActions.handlers.setResponses('users.getUserNotifications', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async getUserNotification(params) {
      const { actions, sdkActions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Users.getUserNotification(params);
        sdkActions.handlers.setResponses('users.getUserNotification', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async getUserUnreadNotificationCount() {
      const { actions, sdkActions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Users.getUserNotifications({
          page: 1,
          size: 0,
          unreadOnly: true,
        });
        sdkActions.handlers.setResponses('users.getUserUnreadNotificationCount', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async markUserNotificationAsRead(params) {
      const { sdkActions } = this.state;
      try {
        const response = await SDK.Users.markUserNotificationAsRead(params);
        sdkActions.handlers.setResponses('users.markUserNotificationAsRead', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    async setUserFromQueryString() {
      const { actions, sdkActions } = this.state;
      const parsedQueryString = queryString.parse(window.location.search);
      const accessToken = parsedQueryString.token || parsedQueryString.accessToken;
      if (accessToken) {
        SDK.Tools.setOption('accessToken', accessToken);
        const decodedToken = jwtDecode(accessToken);

        if (!decodedToken.caseId) {
          await sdkActions.oauth.exchangeToken();
        }

        //Clean password if its a broker
        if (decodedToken.authorities && decodedToken.authorities.includes('ROLE_BROKER')) {
          actions.case.removeFromCase('applicant.pw');
        }
      }

      const caseId = parsedQueryString.caseId || parsedQueryString.caseid || parsedQueryString.caseUuid;
      if (caseId) {
        SDK.Tools.setOption('caseId', caseId);
      }

      return Promise.resolve();
    },
    async createAnonymous() {
      const { sdkActions } = this.state;
      const randomUsernameAndEmail = `${generateUuid()}@ezbob.temp`;
      const dummyPw = 'Pa55w0rd';

      const anonymousUser = {
        username: randomUsernameAndEmail,
        email: randomUsernameAndEmail,
        password: dummyPw,
      };

      try {
        const createAnonymousSDKResults = await SDK.Users.createAnonymous(anonymousUser);
        sdkActions.handlers.setResponses('users.createAnonymous', createAnonymousSDKResults);
        if (createAnonymousSDKResults && createAnonymousSDKResults.data && createAnonymousSDKResults.data.userId) {
          SDK.Tools.setOption('userId', createAnonymousSDKResults.data.userId);
          return Promise.resolve({ createAnonymousSDKResults, anonymousUser });
        }
        return Promise.reject({ createAnonymousSDKResults });
      } catch (e) {
        console.error('createAnonymous', e);
        return Promise.reject(e);
      }
    },
    async create(allowLoginOnSignup, silentCreateUser) {
      const { actions, sdkActions } = this.state;
      const updateUserFields = actions.settings.getUserConfigProp('updateUserFields');
      const user = {
        username: actions.case.getCaseValue(updateUserFields.email),
        password: actions.case.getCaseValue(updateUserFields.pw),
        email: actions.case.getCaseValue(updateUserFields.email),
        roles: ['ROLE_CUSTOMER'],
      };

      try {
        // Create user only if you have email/pass and the user is not already logged in
        const accessToken = SDK.Tools.getOption('accessToken');
        if (accessToken || !user.email || !user.pw) return;

        // Create a user
        const createUserResults = await SDK.Users.create(user, silentCreateUser);
        sdkActions.handlers.setResponses('users.create', createUserResults);

        // Login user
        SDK.Tools.setOption('userId', createUserResults.data.userId);
        await sdkActions.oauth.login(user.username, user.pw, 'password');

        // Attach case to user
        await sdkActions.case.attach();

        // Remove password for internal case there will be only one user creation
        actions.case.resetInCase(updateUserFields.pw);
        return Promise.resolve(createUserResults);
      } catch (e) {
        if (get(e, 'response.data.details.0.violations.0.code') === 'Unique' && allowLoginOnSignup) {
          try {
            await sdkActions.oauth.login(user.username, user.pw, 'password');
          } catch (e) {
            actions.view.setAlerts([{ message: t(`serverMessages.detailedErrors.Unique`), type: 'error' }]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return Promise.reject('login', e);
          }

          // Attach case to user
          const attachResults = await sdkActions.case.attach();
          return Promise.resolve(attachResults);
        } else {
          // actions.view.setAlerts([{message: t(`serverMessages.detailedErrors.Unique`), type: 'error'}]);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        console.error('create user', e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async resetPasswordInitiation(email) {
      const { sdkActions } = this.state;
      try {
        const resetPasswordInitiationResults = await SDK.Users.forgotPassword(email);
        sdkActions.handlers.setResponses('users.resetPasswordInitiation', resetPasswordInitiationResults);
        return Promise.resolve(resetPasswordInitiationResults);
      } catch (e) {
        console.error('resetPasswordInitiation', e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async resetPasswordChange(resetPasswordChangeDTO) {
      const { sdkActions } = this.state;
      try {
        const resetPasswordChangeResults = await SDK.Users.resetPassword(resetPasswordChangeDTO);
        sdkActions.handlers.setResponses('users.resetPasswordChange', resetPasswordChangeResults);
        await sdkActions.oauth.login(resetPasswordChangeDTO.username, resetPasswordChangeDTO.newPassword);
        return Promise.resolve();
      } catch (e) {
        console.error('resetPasswordChange', e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async setPassword(data = {}, contextType) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const opToken = SDK.Tools.getOption('opToken');
        const setPasswordResults = await SDK.Users.setPassword({ ...data, opToken, contextType });
        sdkActions.handlers.setResponses('users.setPassword', setPasswordResults);
        return Promise.resolve(setPasswordResults);
      } catch (e) {
        console.error('setPassword', e);

        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async forgotPassword(data = {}, contextType) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const opToken = SDK.Tools.getOption('opToken');
        const setPasswordResults = await SDK.Users.forgotPassword({ ...data, opToken, contextType });
        sdkActions.handlers.setResponses('users.setPassword', setPasswordResults);
        return Promise.resolve(setPasswordResults);
      } catch (e) {
        console.error('setPassword', e);

        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async logout(overrideLogoutUrl) {
      const { actions, sdkActions } = this.state;
      //This method not expects errors
      const userId = SDK.Tools.getOption('userData') ? SDK.Tools.getOption('userData').user_id : undefined;
      if (userId) {
        const logoutResults = await SDK.Users.logout(userId);
        sdkActions.handlers.setResponses('users.logoutResults', logoutResults);
        actions.userState.resetUserState();
      }
      SDK.Tools.clearOptions();
      clearTimeout(this.state.userState.accessTokenTimeoutRef);
      window.history.replaceState({}, document.title);

      let redirectUrl;

      if (overrideLogoutUrl) {
        redirectUrl = typeof overrideLogoutUrl === 'function' ? overrideLogoutUrl() : overrideLogoutUrl;
      } else {
        redirectUrl = actions.settings.getUserConfigProp('routing.logoutUrl');
      }

      const customerWizardUrl = actions.settings.getUserConfigProp('pageRoutes.customerWizard.url');
      window.removeEventListener('beforeunload', actions.view.handleBeforeUnload);
      redirectToUrl(redirectUrl || customerWizardUrl);
    },
    async update() {
      const { actions, sdkActions } = this.state;
      if (!SDK.Tools.getOption('userData').user_id) return;

      try {
        const updateUserFields = actions.settings.getUserConfigProp('updateUserFields');

        const fields = {
          username: actions.case.getCaseValue(updateUserFields.email),
          password: actions.case.getCaseValue(updateUserFields.pw),
          email: actions.case.getCaseValue(updateUserFields.email),
        };

        if (!fields.pw) return;
        const userId = SDK.Tools.getOption('userData') ? SDK.Tools.getOption('userData').user_id : undefined;
        const updateResults = await SDK.Users.update(userId, fields);
        sdkActions.handlers.setResponses('users.update', updateResults);

        await sdkActions.oauth.login(fields.username, fields.pw, 'password');
        // await sdkActions.case.findCasesByUser();

        actions.case.resetInCase(updateUserFields.pw);
        return Promise.resolve(updateResults);
      } catch (e) {
        console.error(`update: `, e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async verifyEmail(user, opToken) {
      const { sdkActions } = this.state;

      try {
        const verifyEmailResults = await SDK.Users.verifyEmail(user, opToken);

        sdkActions.handlers.setResponses('users.verifyEmail', verifyEmailResults);

        return Promise.resolve(verifyEmailResults.data);
      } catch (e) {
        console.error('verifyEmail', e);

        return Promise.reject(e);
      }
    },
    async getEmailVerificationStatus() {
      const { actions, sdkActions } = this.state;

      try {
        const userData = actions.userState.getUserState('userData');
        const isEmailVerified = await SDK.Users.getEmailVerificationStatus(userData.email);
        sdkActions.handlers.setResponses('users.getEmailVerificationStatus', isEmailVerified);

        return Promise.resolve(isEmailVerified.data);
      } catch (e) {
        console.error(e);
        sdkActions.handlers.setResponses('users.getEmailVerificationStatus', e.response);
        return Promise.reject('getEmailVerificationStatus', e);
      }
    },
    async resendVerificationEmail() {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const userData = actions.userState.getUserState('userData');
        const response = await SDK.Users.resendVerificationEmail(userData.user_id);

        sdkActions.handlers.setResponses('users.resendVerificationEmail', response);

        return Promise.resolve(response.data);
      } catch (e) {
        console.error(e);
        sdkActions.handlers.setResponses('users.resendVerificationEmail', e.response);
        return Promise.reject('resendVerificationEmail', e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async otpSend(otpType, data = {}) {
      const { sdkActions } = this.state;
      try {
        const caseUuid = SDK.Tools.getOption('caseId') ?? SDK.Tools.getOption('invitationCaseId');

        const { id, email, password, phone, channel, guest_token } = data;

        const otpSendResults = await SDK.Users.otpSend(otpType, {
          caseUuid,
          id,
          email: email?.toLowerCase(),
          password,
          phone,
          channel,
          guest_token,
        });
        sdkActions.handlers.setResponses('users.otpSend', otpSendResults);
        return Promise.resolve(otpSendResults);
      } catch (err) {
        console.error(`otpSend`, err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    async otpLogin(opToken, smsCode) {
      const { sdkActions } = this.state;
      try {
        const otpLoginResults = await SDK.Users.otpLogin(opToken, smsCode);
        sdkActions.handlers.setResponses('users.otpLogin', otpLoginResults);
        sdkActions.oauth.mapUserDetailsToSDKOptions(otpLoginResults.data);

        // DL-4500: channel override on OTP login (same as password login above)
        const decodedToken = jwtDecode(otpLoginResults.data.access_token);
        const jwtChannel = decodedToken.channel;
        const currentChannel = SDK.Tools.getOption('channel');
        if (jwtChannel && currentChannel && jwtChannel !== currentChannel) {
          await this.switchChannel(jwtChannel);
        }

        return Promise.resolve(otpLoginResults);
      } catch (err) {
        console.error(`otpLogin`, err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    async getUserPermissions() {
      const { sdkActions } = this.state;
      try {
        const results = await SDK.Users.getUserPermissions();
        sdkActions.handlers.setResponses('users.getUserPermissions', results);
        return Promise.resolve(results);
      } catch (err) {
        console.error(err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    redirectToDashboard(ez2finishwizard = true) {
      const { actions } = this.state;
      const dashboardUrl = actions.settings.getUserConfigProp('routing.dashboardUrl');
      const accessToken = SDK.Tools.getOption('accessToken');
      const refreshToken = SDK.Tools.getOption('refreshToken');
      const caseId = SDK.Tools.getOption('caseId');
      const redirectUrl = `${dashboardUrl}?ez2token=${accessToken}&ez2RefreshToken=${refreshToken}&caseId=${caseId}${
        ez2finishwizard ? `&ez2finishwizard=true` : ``
      }`;

      actions.view.togglePopupLoader(true);
      window.removeEventListener('beforeunload', actions.view.handleBeforeUnload);
      console.info('redirectToDashboard', redirectUrl);
      redirectToUrl(redirectUrl);
    },
    async getUserInfo(userId) {
      const { sdkActions } = this.state;
      try {
        const userInfo = await SDK.Users.getUserInfo(userId);
        sdkActions.handlers.setResponses('users.getUserInfo', userInfo);
        return Promise.resolve(userInfo);
      } catch (err) {
        console.error(err);
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
  },

  externalIdentityManagement: {
    /**
     * Handles the results of an external login process.
     * The function is relevant only for Onboarding where case is relevant.
     * if additional information provides caseId and journeyUuid then a specific case will be retrieved later.
     * otherwise any existing caseId is being removed to force findCaseByUser logic later.
     *
     * @param {Object} externalLoginResults - The results returned from the external login process.
     * @param {Object} externalLoginResults.data - The data object containing additional information.
     * @param {Object} [externalLoginResults.data.additionalInformation] - Additional information from the login results.
     * @param {string} [externalLoginResults.data.additionalInformation.caseUuid] - if available then it will retrieve this case.
     * @param {string} [externalLoginResults.data.additionalInformation.journeyUuid] - if available then it will retrieve this case journey.
     *
     * @returns {void}
     */
    handleExternalLoginResults(externalLoginResults) {
      const { sdkActions } = this.state;

      sdkActions.handlers.setResponses('users.externalLogin', externalLoginResults);

      if (externalLoginResults) {
        sdkActions.oauth.mapUserDetailsToSDKOptions(externalLoginResults.data);
      }

      const { caseUuid, journeyUuid } = get(externalLoginResults, 'data.additionalInformation', {});
      const shouldRetrieveCase = Boolean(caseUuid && journeyUuid);

      if (shouldRetrieveCase) {
        SDK.Tools.setOption('caseId', caseUuid);
        SDK.Tools.setOption('journeyUuid', journeyUuid);
      } else {
        SDK.Tools.removeOption('caseId');
      }
    },

    async externalLogin(SSOData) {
      const { sdkActions } = this.state;

      try {
        const externalLoginResults = await SDK.ExternalIdentityManagement.externalLogin(SSOData);

        sdkActions.externalIdentityManagement.handleExternalLoginResults(externalLoginResults);

        return Promise.resolve(externalLoginResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async externalLoanLogin(SSOData) {
      const { sdkActions } = this.state;

      try {
        const externalLoginResults = await SDK.ExternalIdentityManagement.externalLoanLogin(SSOData);

        sdkActions.externalIdentityManagement.handleExternalLoginResults(externalLoginResults);

        return Promise.resolve(externalLoginResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async externalRenewalLogin(SSOData) {
      const { sdkActions } = this.state;

      try {
        const externalLoginResults = await SDK.ExternalIdentityManagement.externalRenewalLogin(SSOData);

        sdkActions.externalIdentityManagement.handleExternalLoginResults(externalLoginResults);

        return Promise.resolve(externalLoginResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async externalBackofficeLogin(SSOData) {
      const { sdkActions } = this.state;
      try {
        const externalLoginResults = await SDK.ExternalIdentityManagement.externalBackofficeLogin(SSOData);
        sdkActions.handlers.setResponses('users.externalLogin', externalLoginResults);

        if (externalLoginResults) {
          for (let property in externalLoginResults.data) {
            SDK.Tools.setOption(camelCase(property), externalLoginResults.data[property]);
          }
        }
        SDK.Tools.removeOption(`caseId`);
        return Promise.resolve(externalLoginResults);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },

  oauth: {
    async getAppToken(withCookies) {
      const { sdkActions } = this.state;

      try {
        const { channel: channelId, preserveCookies } = parseURLParams();

        const tokenResults = await SDK.Oauth.getAppToken(
          channelId || SDK.Tools.getOption('channelId'),
          preserveCookies || withCookies,
        );

        sdkActions.handlers.setResponses('oauth.appToken', tokenResults);

        const { access_token, token_type, csrfToken } = tokenResults.data;

        const { jti, channel } = jwtDecode(access_token);

        SDK.Tools.storage.setItem('jti', jti);
        SDK.Tools.storage.setItem('productPath', SDK.Tools.getAuthConfig('productPath') ?? '');
        channelId && SDK.Tools.setOption('channelId', channelId);
        channel && SDK.Tools.setOption('channel', channel);
        SDK.Tools.setOption('csrfToken', csrfToken);
        SDK.Tools.setOption('appToken', access_token);
        SDK.Tools.setAuthConfig({ tokenType: token_type });

        return Promise.resolve(access_token);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
    mapUserDetailsToSDKOptions(details) {
      const { actions } = this.state;
      for (let property in details) {
        const propertyValue = details[property];

        if (propertyValue !== null) {
          SDK.Tools.setOption(camelCase(property), propertyValue);
          actions.userState.setUserState(camelCase(property), propertyValue);
        }
      }
    },
    async exchangeToken() {
      const { actions, sdkActions } = this.state;
      try {
        const exchangeTokenResults = await SDK.Oauth.login(undefined, undefined, 'exchange_token');
        SDK.Tools.setOption('accessToken', exchangeTokenResults.data.access_token);
        const decodedToken = jwtDecode(exchangeTokenResults.data.access_token);
        actions.userState.setUserState('authorities', decodedToken.authorities);
        sdkActions.oauth.mapUserDetailsToSDKOptions(decodedToken);

        return Promise.resolve(exchangeTokenResults);
      } catch (e) {
        console.error('exchangeToken', e);
        return Promise.reject(e);
      }
    },
    async login(username, password, grantType = 'password', refreshToken) {
      const { actions, sdkActions } = this.state;
      try {
        let loginResults;
        loginResults = await SDK.Oauth.login(username, password, grantType, refreshToken);
        sdkActions.handlers.setResponses('oauth.loginResults', loginResults);

        const decodedToken = jwtDecode(loginResults.data.access_token);
        actions.userState.setUserState('authorities', decodedToken.authorities);

        //If broker, return data to Login component
        if (decodedToken.authorities.includes('ROLE_BROKER')) {
          return Promise.resolve(loginResults);

          //If EZ1 user then redirect him
        } else if (get(loginResults.data, `user_data`) === `EZ1`) {
          window.removeEventListener('beforeunload', actions.view.handleBeforeUnload);
          const ez1RedirectionUrl = `${actions.settings.getUserConfigProp('routing.vendorEzbob1')}/?ez2token=${
            loginResults.data.access_token
          }`;

          redirectToUrl(ez1RedirectionUrl);
          return Promise.resolve(loginResults);

          //If EZ2 user then return data to Login component
        } else {
          sdkActions.oauth.mapUserDetailsToSDKOptions(loginResults.data);

          // DL-4500: If the login JWT contains a different channel than the current one
          // (e.g. user has an active case on another channel), re-initialize the app config
          // with the correct channel so branding/translations/theme match the case's channel.
          const jwtChannel = decodedToken.channel;
          const currentChannel = SDK.Tools.getOption('channel');
          if (jwtChannel && currentChannel && jwtChannel !== currentChannel) {
            await this.switchChannel(jwtChannel);
          }

          return Promise.resolve(loginResults);
        }
      } catch (e) {
        console.error('sdkActions users.oauth', e, e.response);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },

  configurations: {
    async getConfigurations(namespace) {
      const { actions, sdkActions } = this.state;
      try {
        const getConfigurationsResults = await SDK.Configurations.getConfigurations(namespace);
        sdkActions.handlers.setResponses('configurations.getConfigurations', getConfigurationsResults);
        return Promise.resolve(getConfigurationsResults);
      } catch (e) {
        console.error('initConfigurationsFromServer', e);
        if (!actions.userState.getUserState('demoMode')) {
          actions.view.clearAlerts();
          actions.view.toggleModal({ modalName: 'GeneralError' });
          actions.view.togglePopupLoader(false);
        }
        return Promise.reject(e);
      }
    },
    async getAllConfigurations() {
      const { actions, sdkActions } = this.state;
      try {
        const getAllConfigurationsResults = await SDK.Configurations.getAllConfigurations();
        sdkActions.handlers.setResponses('configurations.getAllConfigurations', getAllConfigurationsResults);
        return Promise.resolve(getAllConfigurationsResults);
      } catch (e) {
        console.error('initConfigurationsFromServer', e);
        if (!actions.userState.getUserState('demoMode')) {
          actions.view.clearAlerts();
          actions.view.toggleModal({ modalName: 'GeneralError' });
          actions.view.togglePopupLoader(false);
        } else {
          actions.userState.setCurrentStep(actions.userState.getInitStep());
        }
        return Promise.reject(e);
      }
    },
  },

  configuration: {
    async getAllConfigurations() {
      const { actions, sdkActions } = this.state;
      const mode = this.state.userState.mode;

      try {
        let getAllConfigurationsResults;
        switch (mode) {
          case UI_FW_MODES.userPortal: {
            getAllConfigurationsResults = await SDK.Configuration.getUserPortalConfigurations();
            break;
          }
          case UI_FW_MODES.backoffice: {
            getAllConfigurationsResults = await SDK.Configuration.getBackofficeConfigurations();
            break;
          }
          default: {
            getAllConfigurationsResults = await SDK.Configuration.getAllConfigurations();
            break;
          }
        }
        sdkActions.handlers.setResponses('configuration.getAllConfigurations', getAllConfigurationsResults);
        return Promise.resolve(getAllConfigurationsResults);
      } catch (e) {
        console.error('initConfigurationsFromServer', e);
        if (!actions.userState.getUserState('demoMode')) {
          actions.view.clearAlerts();
          actions.view.toggleModal({ modalName: 'GeneralError' });
          actions.view.togglePopupLoader(false);
        } else {
          actions.userState.setCurrentStep(actions.userState.getInitStep());
        }
        return Promise.reject(e);
      }
    },
  },

  selfie: {
    async verify(frontPictureUuid, backPictureUuid, selfiePictureUuid, dmsLongitude, dmsLatitude, subjectType) {
      const { actions, sdkActions } = this.state;
      const queryStringParsed = queryString.parse(window.location.search);
      try {
        const data = {
          caseUuid: SDK.Tools.getOption('caseId'),
          frontPictureUuid,
          backPictureUuid,
          selfiePictureUuid,
          dmsLongitude,
          dmsLatitude,
          Subject: {
            subjectType,
            uuid: subjectType === `OFFICER` ? queryStringParsed.officerUuid : undefined,
          },
        };

        let verifyResults;
        if (actions.userState.getUserState('authorities').includes('ROLE_CUSTOMER')) {
          verifyResults = await SDK.Selfie.verifyApplicant(data);
        } else {
          verifyResults = await SDK.Selfie.verifyGuest(data);
        }

        sdkActions.handlers.setResponses('selfie.verify', verifyResults);
        return Promise.resolve(verifyResults);
      } catch (e) {
        console.error('selfie.verify', e);
        return Promise.reject(e);
      }
    },
    async getConfigSelfieCheck() {
      const { sdkActions } = this.state;
      try {
        const getConfigSelfieCheckResults = await SDK.Selfie.getConfigSelfieCheck();

        sdkActions.handlers.setResponses('selfie.getConfigSelfieCheck', getConfigSelfieCheckResults);
        return Promise.resolve(getConfigSelfieCheckResults);
      } catch (e) {
        console.error('selfie.getConfigSelfieCheckResults', e);
        return Promise.reject(e);
      }
    },
  },

  identityVerification: {
    async getIframeUrl(vendor, redirectParams) {
      const { sdkActions } = this.state;

      const caseUuid = SDK.Tools.getOption('caseId');
      const isWebView = checkIsWebView();

      try {
        const getIframeUrlResults = await SDK.IdentityVerification.getIframeUrl(
          caseUuid,
          isWebView,
          vendor,
          redirectParams,
        );
        sdkActions.handlers.setResponses('identityVerification.getIframeUrl', getIframeUrlResults);
        return Promise.resolve(getIframeUrlResults);
      } catch (e) {
        console.error('identityVerification.getIframeUrlResults', e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    // DCJ-9443: open the IDV status SSE stream for the current case. Returns a function that
    // closes it. handlers = { onStatus({ caseUuid, status }), onError(err) }. Sends the current
    // attempt's refId (same source the polling fallback uses) so the BE finishes on this
    // attempt's result and not a prior one still on the case.
    openStatusStream(handlers) {
      const { sdkActions } = this.state;
      const caseUuid = SDK.Tools.getOption('caseId');
      const refId = sdkActions.handlers.getResponses('identityVerification.getIframeUrl.data.refId');
      return SDK.IdentityVerification.openIdvStatusStream(caseUuid, { ...handlers, refId });
    },
  },

  crossChannel: {
    async sendShortUrl(redirectUrl, recipient) {
      const { sdkActions } = this.state;
      const redirectPolicy = `id-verification-sms`;
      const caseId = SDK.Tools.getOption('caseId');
      try {
        const sendResults = await SDK.CrossChannel.sendShortUrl(caseId, redirectUrl, redirectPolicy, recipient);
        sdkActions.handlers.setResponses('crossChannel.sendShortUrl', sendResults);
        return Promise.resolve(sendResults);
      } catch (e) {
        console.error('crossChannel.sendShortUrl', e);
        return Promise.reject(e);
      }
    },
  },

  vat: {
    async getUrl(vrn) {
      const { actions, sdkActions } = this.state;

      try {
        if (!vrn) {
          throw Error('vat getUrl() => vrn is required');
        }

        const caseUuid = SDK.Tools.getOption('caseId');
        const linkedService = actions.settings.getUserConfigProp('custom.vatLinking.service');

        actions.view.togglePopupLoader(true);

        if (!caseUuid) {
          throw Error('vat getUrl() => caseUuid is required');
        }

        if (!linkedService) {
          throw Error('vat getUrl() => linkedService is required');
        }

        const linkResponse = await SDK.Vat.getLink(caseUuid, linkedService, vrn);
        sdkActions.handlers.setResponses('vat.getUrl', linkResponse);

        return Promise.resolve(linkResponse.data);
      } catch (e) {
        return Promise.reject(e);
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async validateVrn(vrn) {
      const { actions, sdkActions } = this.state;
      const caseUuid = SDK.Tools.getOption('caseId');

      try {
        if (!caseUuid) {
          throw Error('vat validateVrn() => caseUuid is required');
        }

        actions.view.clearAlerts();
        const result = await SDK.Vat.validateVrn(caseUuid, vrn);

        return Promise.resolve(result.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },

  handlers: {
    displayGeneralErrorMessage(errorMessage, addMessage = false) {
      this.setState((prevState) => ({
        ...prevState,
        view: {
          ...this.state.view,
          errorMessages: addMessage ? this.state.view.errorMessages.concat(errorMessage) : [errorMessage],
        },
      }));
      this.state.actions.view.togglePopupLoader(false);
    },
    serverErrors(serverErrors, addMessage = false) {
      try {
        const { actions, sdkActions } = this.state;

        // If we got no error
        if (!serverErrors || !serverErrors.response) {
          const errorMessage = i18n.t(`serverMessages.generalErrors.InternalServerError`);
          actions.events.emit(SYSTEM_EVENTS.SERVER_ERROR, { error_text: errorMessage });
          sdkActions.handlers.displayGeneralErrorMessage(errorMessage, addMessage);
          return;
        }

        // If we receive ArrayBuffer then try to make it JSON, readable for the error handler logic
        if (serverErrors.response.data instanceof ArrayBuffer) {
          serverErrors.response.data = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(serverErrors.response.data)),
          );
        }

        // if server error response status is 410 and we have a redirection url - we redirect to the given url
        const redirectUrl = serverErrors.response?.data?.redirectUrl;
        if (serverErrors.response.status === 410 && redirectUrl) {
          redirectToUrl(redirectUrl);
        }

        // if server error response status is 423 and the code is UnderMaintenance - we redirect user to maintenance page
        if (serverErrors.response.status === 423 && serverErrors.response.data.code === 'UnderMaintenance') {
          history.push(actions.settings.getPageRoute('systemMaintenance').url);
        }

        // If we got a general server error and we have a translation
        if (i18n.exists(`serverMessages.generalErrors.${serverErrors.response.data.code}`)) {
          if (serverErrors.response.data.code === 'Violation') {
            let badRequestIndex = 0;
            const propsY = [];

            if (serverErrors.response.data.details && serverErrors.response.data.details.length) {
              let hasUserDataProp = false;
              serverErrors.response.data.details.forEach((detail, key) =>
                detail['@type'] === 'BadRequest' ? (badRequestIndex = key) : undefined,
              );

              const errors = serverErrors.response.data.details[badRequestIndex].violations;
              const clonedUserData = cloneDeep(this.state.userData);
              const alertUiErrorMessages = [];

              errors.forEach((error) => {
                let detailedError;

                if (i18n.exists(`serverMessages.detailedErrors.${error.code}`)) {
                  detailedError = t(`serverMessages.detailedErrors.${error.code}`);
                } else {
                  detailedError = t(`serverMessages.detailedErrors.InvalidContent`);
                }

                hasUserDataProp = get(clonedUserData, error.field);

                //In case this is an error of field
                if (hasUserDataProp) {
                  const elementY = this.state.actions.view.getElementY(error.field);
                  elementY && propsY.push({ propName: error.field, elementY });

                  set(clonedUserData, error.field, {
                    ...get(clonedUserData, error.field),
                    errorMessage: detailedError,
                    validatingStatus: 'invalid',
                  });
                } else {
                  actions.events.emit(SYSTEM_EVENTS.SERVER_ERROR, {
                    error_text: detailedError,
                    error_code: serverErrors.response.data.code,
                  });
                  alertUiErrorMessages.push({ message: detailedError, type: 'error' });
                }
              });

              this.setState((prevState) => ({ ...prevState, userData: clonedUserData }));

              addMessage ? actions.view.addAlerts(alertUiErrorMessages) : actions.view.setAlerts(alertUiErrorMessages);

              if (hasUserDataProp) {
                propsY.sort((a, b) => (a.elementY < b.elementY ? -1 : 1));
                actions.view.scrollToWidget(propsY[0].propName);
              }

              return errors;
            }
          } else {
            const errorMessage = t(`serverMessages.generalErrors.${serverErrors.response.data.code}`);
            actions.events.emit(SYSTEM_EVENTS.SERVER_ERROR, {
              error_text: errorMessage,
              error_code: serverErrors.response.data.code,
            });
            actions.view.setAlerts([{ message: errorMessage, type: 'error' }]);
          }
        } else {
          let errorMessage = i18n.t(`serverMessages.generalErrors.InternalServerError`);
          actions.events.emit(SYSTEM_EVENTS.SERVER_ERROR, {
            error_text: errorMessage,
            error_code: serverErrors.response.data.code,
          });
          sdkActions.handlers.displayGeneralErrorMessage(errorMessage);
        }

        return serverErrors;
      } catch (e) {
        return serverErrors;
      }
    },
    setResponses(name, response) {
      if (response && name) {
        this.setState((prevState) => {
          const sdkResponses = cloneDeep(prevState.sdkResponses);
          set(sdkResponses, name, { data: response.data, headers: response.headers });
          return { ...prevState, sdkResponses };
        });
        console.info(`SDK Action:`, name, { data: response.data, headers: response.headers });
      }
    },
    getResponses(name) {
      return get(this.state.sdkResponses, name);
    },
    clearResponses(name) {
      this.setState((prevState) => {
        let sdkResponses = cloneDeep(prevState.sdkResponses);
        if (name) {
          unset(sdkResponses, name);
        } else {
          sdkResponses = {};
        }
        return { ...prevState, sdkResponses };
      });
    },
  },

  decision: {
    async calculateOffer(amount, term, policy) {
      const { sdkActions } = this.state;
      const caseUuid = SDK.Tools.getOption('caseId');
      try {
        const { data } = await SDK.Decision.calculateOffer(caseUuid, amount, term, policy);
        sdkActions.handlers.setResponses('decision.calculateOffer', data);
        return Promise.resolve(data);
      } catch (e) {
        console.error(e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },
  extensions: {
    async getRejectionRules(ruleSetName) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const caseId = SDK.Tools.getOption('caseId');
        const response = await SDK.Extensions.getRejectionRules(caseId, { ruleSetName });

        sdkActions.handlers.setResponses('extensions.getRejectionRules', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  backoffice: {
    async getProducts() {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.getProducts();
        sdkActions.handlers.setResponses('backoffice.getProducts', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getProductByCase(caseUuid) {
      const { sdkActions } = this.state;
      try {
        const response = await SDK.Backoffice.getProductByCase(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getProductByCase', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getLoans(data, params, signal) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.getLoans(data, params, signal);

        sdkActions.handlers.setResponses('backoffice.getLoans', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getLoansCount() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getLoansCount();

        sdkActions.handlers.setResponses('backoffice.getLoansCount', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async pushForwardLoan(refNum, data, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.pushForwardLoan(refNum, data, params);

        sdkActions.handlers.setResponses('backoffice.pushForwardLoan', response);

        return Promise.resolve(response.data);
      } catch (e) {
        console.error(e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async splitLoan(refNum, data, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.splitLoan(refNum, data, params);

        sdkActions.handlers.setResponses('backoffice.splitLoan', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getCases(data, params) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.getCases(data, params);

        sdkActions.handlers.setResponses('backoffice.getCases', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getCasesCount() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getCasesCount();

        sdkActions.handlers.setResponses('backoffice.getCasesCount', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getCaseDetails(refNum) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getCaseDetails(refNum);
        sdkActions.handlers.setResponses('backoffice.getCaseDetails', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getLoansByCase(refNum) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getLoansByCase(refNum);
        sdkActions.handlers.setResponses('backoffice.getLoansByCase', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getCaseDetailsByLoanRef(refNum) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getCaseDetailsByLoanRef(refNum);
        sdkActions.handlers.setResponses('backoffice.getCaseDetailsByLoanRef', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getLoanState(refNum, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getLoanState(refNum, params);
        sdkActions.handlers.setResponses('backoffice.getLoanState', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getInstalments(refNum, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getInstalments(refNum, params);
        sdkActions.handlers.setResponses('backoffice.getInstalments', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getTransactions(refNum, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getTransactions(refNum, params);
        sdkActions.handlers.setResponses('backoffice.getTransactions', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getRepaymentHistory(refNum, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getRepaymentHistory(refNum, params);
        sdkActions.handlers.setResponses('backoffice.getRepaymentHistory', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getSignedDocument(docID) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getSignedDocument(docID);
        sdkActions.handlers.setResponses('backoffice.getSignedDocument', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async processRepayment(refNum, paymentData, performSave) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.processRepayment(refNum, paymentData, performSave);
        sdkActions.handlers.setResponses('backoffice.processRepayment', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async createRepayment(refNum, paymentData) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.createRepayment(refNum, paymentData);
        sdkActions.handlers.setResponses('backoffice.createRepayment', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async unfreezeAutoRepayments(refNum, data) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.unfreezeAutoRepayment(refNum, data);
        sdkActions.handlers.setResponses('backoffice.unfreezeAutoRepayment', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async freezeAutoRepayments(refNum, data) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.freezeAutoRepayment(refNum, data);
        sdkActions.handlers.setResponses('backoffice.freezeAutoRepayment', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async withdrawCase(caseUuid) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.updateCaseStatus(caseUuid, { status: 'WITHDRAWN' });
        sdkActions.handlers.setResponses('backoffice.withdrawCase', response);
        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async updateDecision(caseUuid, data) {
      const { sdkActions, actions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.updateDecision(caseUuid, data);
        sdkActions.handlers.setResponses('backoffice.updateDecision', response);
        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getDefaultReasons() {
      const { sdkActions } = this.state;
      try {
        const response = await SDK.Backoffice.getDefaultReasons();
        sdkActions.handlers.setResponses('backoffice.getDefaultReasons', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async makeDefault(refNum, data) {
      const { sdkActions, actions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.makeDefault(refNum, data);
        sdkActions.handlers.setResponses('backoffice.makeDefault', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async addRefund(refNum, data) {
      const { sdkActions, actions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.addRefund(refNum, data);
        sdkActions.handlers.setResponses('backoffice.addRefund', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getOcrDetailsExtended(caseUuid, fileType) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getOcrDetailsExtended(caseUuid, fileType);
        sdkActions.handlers.setResponses('backoffice.getOcrDetailsExtended', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getOCRDetails(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getOCRDetails(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getOCRDetails', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async updateOCRDetails(caseUuid, data) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.updateOCRDetails(caseUuid, data);
        sdkActions.handlers.setResponses('backoffice.updateOCRDetails', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getRulesetOutcome(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getRulesetOutcome(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getRulesetOutcome', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getOCRHistory(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getOCRHistory(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getOCRHistory', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getOCRRuleDetails(caseUuid, primaryFileType) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getOCRRuleDetails(caseUuid, primaryFileType);
        sdkActions.handlers.setResponses('backoffice.getRulesetAudit', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getRulesetAudit(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getRulesetAudit(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getRulesetAudit', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async progress(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.progress(caseUuid);
        sdkActions.handlers.setResponses('backoffice.progress', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async runValidation(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.runValidation(caseUuid);
        sdkActions.handlers.setResponses('backoffice.runValidation', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async closeCase(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.closeCase(caseUuid);
        sdkActions.handlers.setResponses('backoffice.closeCase', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getAllWhitelistedNumbers() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getAllWhitelistedNumbers();
        sdkActions.handlers.setResponses('backoffice.getAllWhitelistedNumbers', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getWhitelistedNumberByNumber(number) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getWhitelistedNumberByNumber(number);
        sdkActions.handlers.setResponses('backoffice.getWhitelistedNumberByNumber', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async addWhitelistedNumber({ phoneNumber }) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.addWhitelistedNumber({ phoneNumber });
        sdkActions.handlers.setResponses('backoffice.addWhitelistedNumber', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async removeWhitelistedNumber(phoneNumber) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.removeWhitelistedNumber(phoneNumber);
        sdkActions.handlers.setResponses('backoffice.removeWhitelistedNumber', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async createManualRepayment(refNum, params) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.createManualRepayment(refNum, params);

        sdkActions.handlers.setResponses('backoffice.manualRepayment', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async cancelLoan(refNum, data) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.cancelLoan(refNum, data);
        sdkActions.handlers.setResponses('backoffice.cancelLoan', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getWhitelistByType(type) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.getWhitelistByType(type);
        sdkActions.handlers.setResponses('backoffice.getWhitelistByType', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getUserDataByType(type, userIdentification) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.getUserDataByType(type, userIdentification);
        sdkActions.handlers.setResponses('backoffice.getUserDataByType', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async getResetHistory(params) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.getResetHistory(params);
        sdkActions.handlers.setResponses('backoffice.getResetHistory', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async resetUser(comment, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.resetUser(comment, params);
        sdkActions.handlers.setResponses('backoffice.resetUser', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async updateMarker(caseUuid, loanRef, markerValue) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.updateMarker(caseUuid, loanRef, markerValue);
        sdkActions.handlers.setResponses('backoffice.updateMarker', response);
        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async reportLoanStatus(refNum, checked) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.reportLoanStatus(refNum, checked);
        sdkActions.handlers.setResponses('backoffice.reportLoanStatus', response);
        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async updateCaseStatus(caseUuid, status) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.updateCaseStatus(caseUuid, { status });
        sdkActions.handlers.setResponses('backoffice.updateCaseStatus', response);
        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getCaseJourneys(caseUuid) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.getCaseJourneys(caseUuid);
        sdkActions.handlers.setResponses('backoffice.getCaseJourneys', response);
        return Promise.resolve(response.data);
      } catch (e) {
        console.log({ e });
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async updateCaseJourney(caseUuid, journeyUuid, { stepName, uiStep }) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.updateCaseJourney(caseUuid, journeyUuid, { stepName, uiStep });
        sdkActions.handlers.setResponses('backoffice.updateCaseJourney', response);
        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async freezePayment(refNum, performSave) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);

        const response = await SDK.Backoffice.freezePayment(refNum, performSave);

        sdkActions.handlers.setResponses('backoffice.freezePayment', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async sendRepaymentDocuments(data) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.sendRepaymentDocuments(data);
        sdkActions.handlers.setResponses('backoffice.sendRepaymentDocuments', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async loanReschedule(refNum, data, params) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.loanReschedule(refNum, data, params);

        sdkActions.handlers.setResponses('backoffice.loanReschedule', response);

        return Promise.resolve(response.data);
      } catch (e) {
        console.error(e);
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async setCourtCaseOpenDate(caseUuid, courtCaseOpenDateStr) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.setCourtCaseOpenDate(caseUuid, courtCaseOpenDateStr);
        sdkActions.handlers.setResponses('backoffice.setCourtCaseOpenDate', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async updateDebtClassification(refNum, product, body) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.updateDebtClassification(refNum, product, body);
        sdkActions.handlers.setResponses('backoffice.updateDebtClassification', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getValiditAiChannelsConfig(tenant, product) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Backoffice.getValiditAiChannelsConfig(tenant, product);
        sdkActions.handlers.setResponses('backoffice.getValiditAiChannelsConfig', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },

    async saveValiditAiChannelsConfig(tenant, payload, product) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Backoffice.saveValiditAiChannelsConfig(tenant, payload, product);
        sdkActions.handlers.setResponses('backoffice.saveValiditAiChannelsConfig', response);

        return Promise.resolve(response.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },

  cmeLoan: {
    async getLoansByCaseId(caseUuid) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.CMELoan.getLoansByCaseId(caseUuid);

        sdkActions.handlers.setResponses('cmeLoan.getLoansByCaseId', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async getLoanStateByReferenceNumber(refnum, finalState) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.CMELoan.getLoanStateByReferenceNumber(refnum, finalState);

        sdkActions.handlers.setResponses('cmeLoan.getLoanStateByReferenceNumber', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
    async getLoans(onlyActive) {
      const { actions, sdkActions } = this.state;

      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.CMELoan.getLoans({ onlyActive });

        sdkActions.handlers.setResponses('cmeLoan.getLoans', response);

        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  userPortal: {
    async getCase(caseUuid, journeyUuid) {
      const { actions, sdkActions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.Case.retrieve(caseUuid, journeyUuid);
        sdkActions.handlers.setResponses('userPortal.getCase', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  crm: {
    async sendCRMForm(data) {
      const { actions, sdkActions } = this.state;
      try {
        actions.view.togglePopupLoader(true);
        const response = await SDK.CRM.sendCRMForm(data);
        sdkActions.handlers.setResponses('CRM.sendCRmForm', response);
        return Promise.resolve(response.data);
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      } finally {
        actions.view.togglePopupLoader(false);
      }
    },
  },

  adminAgent: {
    async getApplicationConfigurations() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.getData('application-configurations');
        sdkActions.handlers.setResponses('adminAgent.getApplicationConfigurations', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getStateFlows() {
      const sdkActions = this.state.sdkActions;

      try {
        const response = await SDK.AdminAgent.getData('state-flow', { hint: 'no-profiles' });
        sdkActions.handlers.setResponses('adminAgent.getStateFlows', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getApplicationTranslations() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.getData('application-translations');
        sdkActions.handlers.setResponses('adminAgent.getApplicationTranslations', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async importTranslationsFromLowerEnvironment() {
      const { sdkActions } = this.state;
      try {
        const response = await SDK.AdminAgent.getData('external-plugin', {
          internalScope: 'application-translations',
          product: SDK.Tools.getOption('product'),
        });
        sdkActions.handlers.setResponses('adminAgent.importTranslationsFromLowerEnvironment', response);
        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async applyApplicationTranslations(data, { operation }) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.postData('application-translations', { operation }, data);
        sdkActions.handlers.setResponses('adminAgent.applyApplicationTranslations', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getMaintenance(scope = 'maintenance') {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.getData(scope);
        sdkActions.handlers.setResponses('adminAgent.getMaintenance', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async applyMaintenance(data, { operation }, scope = 'maintenance') {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.postData(scope, { operation }, data);
        sdkActions.handlers.setResponses('adminAgent.applyMaintenance', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async getPilotRules() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.getData('rules');
        sdkActions.handlers.setResponses('adminAgent.getPilotRules', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async applyPilotRules(data, { operation, uuid }) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.postData('rules', { operation, uuid }, data);
        sdkActions.handlers.setResponses('adminAgent.applyPilotRules', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    async applyCaseHistoryReset(data, { operation }) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.AdminAgent.postData('CASE_HISTORY_RESET', { operation }, data);
        sdkActions.handlers.setResponses('adminAgent.applyCaseHistoryReset', response);

        return Promise.resolve(response?.data);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },

  system: {
    async getMaintenanceData() {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.System.getMaintenanceData();

        sdkActions.handlers.setResponses('system.getMaintenanceData', response);

        const productMaintenance = response?.data.find(
          ({ maintenanceType, startDate }) => maintenanceType === 'PRODUCT' && startDate,
        );

        return Promise.resolve(productMaintenance);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
    async initializeApp() {
      const { sdkActions, actions } = this.state;

      try {
        const response = await SDK.System.getTokenData();

        sdkActions.handlers.setResponses('tokenData', response.data);

        // GRN-6822: surface the backend opaque-refresh feature flag so the SDK 401 interceptor only
        // attempts grant_type=refresh_token for tenants that actually support it (Greenlend). Set
        // unconditionally at bootstrap — before the jti branching below and before any authenticated
        // call — so it is available for every later 401, including on a page reload.
        SDK.Tools.setAuthConfig({ opaqueRefreshEnabled: response.data.opaque_refresh_enabled });

        const { jti } = response.data;
        const currentJti = SDK.Tools.storage.getItem('jti');

        const urlProductPath = SDK.Tools.getAuthConfig('productPath') ?? '';
        const storedProductPath = SDK.Tools.storage.getItem('productPath') ?? '';
        const productMatches = urlProductPath === storedProductPath;

        let shouldShowTabWarning = false;

        if (jti && jti === currentJti && productMatches) {
          sdkActions.oauth.mapUserDetailsToSDKOptions(response.data);
        } else {
          if (!productMatches) {
            // Product switch: drop the previous product's user/case/token state
            // before re-issuing under the new product. jti-only mismatches
            // (multi-tab race on the same product) keep userData intact.
            SDK.Tools.clearOptions();
            actions.userState.setUserState('userData', {});
          }

          // Preserve session cookies (user_token/case_token/refresh) when a live
          // same-product session is being recovered on reload — a bare mint would
          // wipe them and force an unrecoverable logout (GRN-6822 C13/§4.3b). On a
          // product switch (productMatches=false) the previous product's cookies
          // are intentionally dropped.
          // GRN-7759: preserve only when this tab already held a session (reload / app-token
          // recovery). A tab with no stored identity is a new visitor on a shared device, so a
          // bare mint lets the backend expire the previous applicant's case_token/user_token.
          const hadSession = !!SDK.Tools.storage.getItem('jti') || !!SDK.Tools.storage.getItem('userData');

          await sdkActions.oauth.getAppToken(productMatches && hadSession);

          if (jti && jti !== currentJti && productMatches) {
            const tabTime = localStorage.getItem('activeTabTime');

            shouldShowTabWarning = moment().diff(moment(tabTime), 'seconds') < 5;
          }
        }

        return Promise.resolve({ data: response.data, shouldShowTabWarning });
      } catch (err) {
        return Promise.reject(sdkActions.handlers.serverErrors(err));
      }
    },
  },

  channels: {
    async consumeOneTimeToken(OTTData, config, flow) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Channels.consumeOneTimeToken(OTTData, config, flow);

        sdkActions.handlers.setResponses('channels.consumeOneTimeToken', response);

        const caseToken = response?.data?.onboardingCase?.caseToken;
        if (caseToken) {
          SDK.Tools.setOption('caseToken', caseToken);
        }

        if (response?.data?.onboardingCase?.uuid) {
          SDK.Tools.setOption('caseId', response.data.onboardingCase.uuid);
        }

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async clearSession(config) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Channels.clearSession(config);

        sdkActions.handlers.setResponses('channels.clearSession', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(e);
      }
    },
  },

  discount: {
    /**
     * Retrieves the next IL business day for a given date.
     *
     * @param {string} [dateTime] - The date and time in ISO format: yyyy-MM-ddThh:MM:ss (e.g., '2025-10-21T00:00:00').
     * @returns {Promise} A promise that resolves with the server's response containing the next business day information.
     */
    async getNextBusinessDay(dateTime) {
      const { sdkActions } = this.state;

      try {
        const response = await SDK.Discount.getNextBusinessDay(dateTime);

        sdkActions.handlers.setResponses('discount.getNextBusinessDay', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },

    /**
     * Retrieves legal credit consent statement document for current case.
     * Case UUID is resolved internally for better control and early exit.
     */
    async getStatementDocuments() {
      const { sdkActions } = this.state;

      try {
        const caseUuid = SDK.Tools.getOption('caseId');
        if (!caseUuid) {
          throw Error('discount getStatementDocuments() => caseUuid is required');
        }

        const response = await SDK.Discount.getStatementDocuments(caseUuid);
        sdkActions.handlers.setResponses('discount.getStatementDocuments', response);

        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(sdkActions.handlers.serverErrors(e));
      }
    },
  },
};
